import type { CabinetShape } from "building-types/casework/caseworkDescriptor";
import {
  CaseworkVerticalDatumAnchor,
  CaseworkVerticalAlignment,
  CaseworkHorizontalAlignment,
  CaseworkLateralAlignment,
} from "building-types/datums/datumsTypesEnum";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type {
  HorizontalEdgeParamNoExpr,
  HorizontalEdgePlacementNoExpr,
  LateralEdgePlacement,
} from "building-types/entities/edgePlacementTypes";
import type { RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { PaletteType } from "building-types/materials/paletteType";
import type { Formula } from "building-types/options/formula";
import type { RoomLayoutEligibleDocEntity } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { LineAnchor } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { PlanNoteOrientation, CabinetType, ShelvingType, BlindPanelSide } from "./caseworkTypesEnum";

export interface WallCaseworkPlanNote {
  readonly type: "PLAN";
  readonly note: string;
  readonly position: Vec2;
  readonly orientation: PlanNoteOrientation;
}
/**
 * Multi-edge (DYNAMIC) casework is being phased out, so only the fixed type is upgradable to the wall layer placement system
 */

export type WallCasework = WallCaseworkFixed | WallCaseworkFixedDeprecated | WallCaseworkDynamicDeprecated;

/**
 * `depth`, `height`, `verticalOffset` and `material` are optional: an absent field means "inherit from
 * `CaseworkSettings`", resolved at generation time by `resolveCaseworkShape` in
 * `doc/elements/caseworkSettingsResolution`. A present field is an override the user typed. Only
 * casework created with the Casework V2 flag on omits them — every pre-existing entity carries
 * explicit values, so it keeps its geometry.
 *
 * An inherited required member cannot be relaxed to optional, so `verticalOffset` and `material` are
 * kept out of the bases entirely rather than `Omit`ted from them. Both alternatives break the doc
 * schema generator, which only emits a named `id(...)` reference for a property whose type is exactly
 * the alias:
 * - `Omit<Base, "field">` is a mapped type it cannot see through, so it inlines every shared reference
 *   and silently drops `parentId`'s `DocEntityParentId` hierarchy metadata.
 * - redeclaring `material?` on the subtype inlines `PaletteMaterialSlot`'s whole 130-member union.
 *
 * So this extends the two placement interfaces that exclude `verticalOffset`, and `material` is
 * declared per concrete casework type — optional here, required on the two deprecated types.
 */
export interface WallCaseworkFixed extends HorizontalEdgePlacementNoExpr, LateralEdgePlacement, WallCaseworkBase {
  readonly type: "FIXED_V2";
  readonly width: number;
  readonly catalogDescriptorId?: ForeignEntityId<"CASEWORK_DESCRIPTOR">;
  readonly horizontalAlignment: CaseworkHorizontalAlignment;
  readonly verticalAlignment: CaseworkVerticalAlignment;
  readonly lateralAlignment: CaseworkLateralAlignment;
  readonly verticalDatum: CaseworkVerticalDatumAnchor;
  readonly useWallStripMaxOffset?: boolean;
  readonly depth?: number;
  readonly height?: number;
  readonly verticalOffset?: number;
  // Declared here, not before `width`: the schema generator emits a named `id("PaletteMaterialSlot")`
  // reference only if the alias has already been reached by this point in the type graph, and inlines
  // its whole 130-member union otherwise.
  readonly material?: PaletteMaterialSlot;
  readonly lateralOffset: number;
  /** Yaw about the placement anchor, clockwise in plan. Absent reads as 0. */
  readonly rotationDegrees?: number;
}

/**
 * @deprecated
 */
export interface WallCaseworkFixedDeprecated
  extends HorizontalEdgeParamNoExpr,
    WallCaseworkBase,
    FormulaBasedCaseworkSize {
  readonly type: "FIXED";
  readonly edgeId: string;
  readonly width: number;
  readonly catalogDescriptorId?: ForeignEntityId<"CASEWORK_DESCRIPTOR">;
  readonly paramAnchor: LineAnchor;
  readonly heightFromCeiling: boolean;
  readonly material: PaletteMaterialSlot;
}

/**
 * @deprecated
 */
export interface WallCaseworkDynamicDeprecated extends WallCaseworkBase, FormulaBasedCaseworkSize {
  readonly type: "DYNAMIC";
  readonly edgeIds: string[];
  readonly paramAnchor: LineAnchor;
  readonly placementStart: HorizontalEdgeParamNoExpr;
  readonly placementEnd: HorizontalEdgeParamNoExpr;
  readonly heightFromCeiling: boolean;
  readonly material: PaletteMaterialSlot;
}

/**
 * @deprecated
 */
interface FormulaBasedCaseworkSize {
  readonly depth: number | Formula;
  readonly height: number | Formula;
  readonly verticalOffset: number | Formula;
  readonly lateralOffset: number | Formula;
}

interface WallCaseworkBase extends FormulaDisabledDocEntityBase<"CASEWORK">, RoomLayoutEligibleDocEntity {
  readonly id: PrimaryEntityId<"CASEWORK">;
  readonly handleOverride?: RoomLayoutInstanceHandle | undefined;
  readonly caseworkType?: CabinetType | ShelvingType;
  /**
   * Instance-level override of the catalog descriptor's cabinet box shape (rectangular / L-shaped /
   * angled). Undefined falls back to the catalog descriptor's `shape`, which itself reads as `NONE`
   * when absent. Only meaningful for cabinet instances — see `CaseworkSuperType.CABINET`.
   */
  readonly shape?: CabinetShape;
  readonly cabinetDoorsOnLeft?: boolean;
  readonly cabinetDoorsOnRight?: boolean;
  readonly closetShelfRod?: boolean;
  readonly notes: WallCaseworkPlanNote[];
  readonly paletteType: PaletteType;
  readonly floorMaterialOverride?: InteriorFloorMaterialOverride;
  readonly lineStyle?: CaseworkLineStyle; // undefined means use SOLID
  readonly blindPanelSide?: BlindPanelSide;
  readonly blindPanelExtension?: number;
  readonly adjacentCabinetFiller?: boolean;
  readonly adjacentCabinetFillerWidth?: number;
  readonly tag?: string;
  readonly tagOffset?: number;
  readonly parentId?: DocEntityParentId<"ROOM" | "ROOM_LAYOUT">;
}

export enum CaseworkLineStyle {
  SOLID = "SOLID",
  DASHED = "DASHED",
  POCHE = "POCHE",
  NONE = "NONE",
}

export {
  CaseworkVerticalDatumAnchor,
  CaseworkVerticalAlignment,
  CaseworkHorizontalAlignment,
  CaseworkLateralAlignment,
};
